import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdContentCopy, MdOutlineClear } from 'react-icons/md'
import { PortalWithState } from 'react-portal'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IPurchase } from '~/types/app-purchase'
import type { IConnectionData, IEmailCredentials } from '~/types/app-user'

import {
  connectionCodesColumns,
  connectionsColumns,
  credentialsColumns,
  featuresColumns,
  purchasesColumns,
} from './columns'
import PanelContent from './PanelContent'

import GetTokenModal from '../Modals/GetTokenModal'
import UpdatePurchaseModal from '../Modals/UpdatePurchaseModal'
import VerifyMailModal from '../Modals/VerifyMailModal'

import ActionMenu from '~/components/ActionMenu'
import BackNavigation from '~/components/BackNavigation'
import { AppUserStatusBadge, EnvironmentBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import DetailSection from '~/components/DetailSection'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import Panel from '~/components/Panel'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useDeleteAppsUserPurchaseMutation } from '~/queries/app-purchases'
import { useAppUserDetailsQuery, keys as appsUsersKeys, useDeleteAppUserAccountMutation } from '~/queries/app-users'
import { USER_TYPE } from '~/types/app-user'
import { formatFeatures } from '~/utils/app-features'
import { handleGenericError } from '~/utils/error'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'
import EditUserModal from '~/views/Accounts/Modals/EditAppsUserModal'
import ResendVerificationMailModal from '~/views/Accounts/Modals/ResendVerificationMailModal'

import './styles.scss'

const noOp = () => undefined

interface IEmailCredentialsRowProps {
  credentials: IEmailCredentials
  setEmailRowActionMenu: (
    credentials: IEmailCredentials,
    openPortal: (event?: any) => void
  ) => { actions: { action: () => void; label: string }[] }
}

const EmailCredentialsRow = ({ credentials, setEmailRowActionMenu }: IEmailCredentialsRowProps): ReactElement => {
  /** Local state */

  const isUserAuthorized = useIsUserAuthorized([AUTHORIZATIONS.MWM_USERS.ACCOUNT.WRITE])

  const connectionCodesTableRef = useRef<HTMLDivElement>(null)

  const { connection_codes, created_at, email, is_verified, updated_at } = credentials

  /** Handlers */

  const handleCopy = (value: string) => () => copyText(value, 'Copied to clipboard!')

  /** Render */

  return (
    <tr className="table-row">
      <td className="table-cell table-cell--flexed table-cell--clickable" onClick={handleCopy(email)}>
        {email} {is_verified ? <div className="user-verified-badge">Verified</div> : null}
      </td>
      <td className="table-cell table-position--right">{formatDate(created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}</td>
      <td className="table-cell table-position--right">{formatDate(updated_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}</td>
      <td className="table-cell table-cell__actions">
        <PortalWithState closeOnEsc>
          {({ closePortal, openPortal, portal }) => (
            <>
              <ActionMenu disabled={!isUserAuthorized} menu={setEmailRowActionMenu(credentials, openPortal)} />
              {portal(
                <div className="email-codes-portal__overlay">
                  <div className="email-codes-portal">
                    <Button className="close-btn" ghost onClick={closePortal}>
                      <MdOutlineClear color="#626262" size={17} />
                    </Button>
                    <div className="email-codes-portal__title">Connection codes ({connection_codes?.length || 0})</div>
                    {connection_codes && connection_codes.length > 0 ? (
                      <TableWrapper
                        allowContextMenuWithControlKey
                        columnDefs={connectionCodesColumns}
                        context={{ dateTimeCellRendererFallbackMessage: 'Never' }}
                        entriesName="connection-codes"
                        rowData={connection_codes}
                        suppressRowHoverHighlight
                        tableRef={connectionCodesTableRef}
                        withPagination={false}
                      />
                    ) : (
                      <div>No code</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </PortalWithState>
      </td>
    </tr>
  )
}

const AppsUsersDetails = (): ReactElement => {
  /** Local state */

  const location = useLocation()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { searchParamsObject } = useSearchParamsContext()

  const search = searchParamsObject.search || ''

  const { env = USER_TYPE.DEV, userId = '' } = useParams<{ env: USER_TYPE; userId: string }>()

  const purchasesTableRef = useRef<HTMLDivElement>(null)

  const featuresTableRef = useRef<HTMLDivElement>(null)

  const credentialsTableRef = useRef<HTMLDivElement>(null)

  const connectionsTableRef = useRef<HTMLDivElement>(null)

  const [isEditModalOpen, setEditModalState] = useState(false)

  const [isUpdatePurchaseModalOpen, setUpdatePurchaseModalState] = useState(false)

  const [isResendModalOpen, setResendModalState] = useState(false)

  const [isVerifyModalOpen, setVerifyModalState] = useState(false)

  const [isDeletePurchaseModalOpen, setDeleteModalState] = useState(false)

  const [isDeleteAccountModalOpen, setDeleteAccountModalState] = useState(false)

  const [isTokenModalOpen, setTokenModalState] = useState(false)

  const [isCreatePurchasePanelOpen, setIsCreatePurchasePanelOpen] = useState(false)

  const [selectedEmail, setSelectedEmail] = useState('')

  const [selectedPurchase, setSelectedPurchase] = useState<IPurchase>()

  const [connectionData, setConnectionData] = useState<IConnectionData>()

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  /** Queries & Mutations */

  const { data: user, isLoading } = useAppUserDetailsQuery(env, userId)

  const formattedFeatures = user
    ? Object.keys(user?.features).length > 0
      ? formatFeatures(user.features)
      : null
    : null

  const { mutateAsync: deleteAppsUserPurchase } = useDeleteAppsUserPurchaseMutation()

  const { mutateAsync: deleteAppsUserAccount } = useDeleteAppUserAccountMutation()

  /** Effects */

  useEffect(() => {
    if (!isEditModalOpen && !isResendModalOpen && !isVerifyModalOpen) setSelectedEmail('')
  }, [isEditModalOpen, isResendModalOpen, isVerifyModalOpen])

  useEffect(() => {
    if (!isTokenModalOpen) setConnectionData(undefined)
  }, [isTokenModalOpen])

  const handleClickOutside = useCallback(
    (event: any) => {
      if ((panelRef && panelRef.contains(event.target)) || !document.getElementById('root')?.contains(event.target)) {
        return
      }
      handleCreatePurchasePanelState(false)
    },
    [panelRef]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [panelRef, handleClickOutside])

  /** Handlers */

  const setEmailRowActionMenu = useCallback((credentials: IEmailCredentials, openPortal: (event?: any) => void) => {
    const menu = {
      actions: [
        {
          action: () => {
            setSelectedEmail(credentials.email)
            setEditModalState(true)
          },
          label: 'Edit',
        },
        {
          action: () => openPortal(),
          label: 'See connection codes',
        },
      ],
    }

    if (!credentials.is_verified) {
      menu.actions = [
        ...menu.actions,
        {
          action: () => {
            setSelectedEmail(credentials.email)
            setResendModalState(true)
          },
          label: 'Resend verification email',
        },
        {
          action: () => {
            setSelectedEmail(credentials.email)
            setVerifyModalState(true)
          },
          label: 'Verify email',
        },
      ]
    }

    return menu
  }, [])

  const handleGetToken = (connectionData: IConnectionData) => {
    setConnectionData(connectionData)
    setTokenModalState(true)
  }

  const handleDeletePurchase = (value: IPurchase) => {
    setDeleteModalState(true)
    setSelectedPurchase(value)
  }

  const handleDeletePurchaseConfirm = async () => {
    if (user && selectedPurchase) {
      try {
        await deleteAppsUserPurchase({
          env,
          purchaseId: selectedPurchase.purchase_id,
          userId: user.user_id,
        })
        queryClient.invalidateQueries({ queryKey: appsUsersKeys.details(env, user.user_id) })
        displayToast('The selected purchase was successfully deleted', 'success')
        setDeleteModalState(false)
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleEditPurchase = (value: IPurchase) => {
    setUpdatePurchaseModalState(true)
    setSelectedPurchase(value)
  }

  const handleCopy = (value: string) => () => copyText(value, 'Copied to clipboard!')

  const handleEditModalState = (isOpen: boolean) => () => setEditModalState(isOpen)

  const handleDeletePurchaseModalState = (isOpen: boolean) => () => setDeleteModalState(isOpen)

  const handleUpdatePurchaseModalState = (isOpen: boolean) => () => setUpdatePurchaseModalState(isOpen)

  const handleEditFailure = (message: string) => {
    setEditModalState(false)
    displayToast(message, 'error')
  }

  const handleEditSuccess = (message: string) => {
    setEditModalState(false)
    displayToast(message, 'success')
  }

  const handleResendModalState = (isOpen: boolean) => () => setResendModalState(isOpen)

  const handleResendFailure = () => {
    setResendModalState(false)
    displayToast('Verification email could not be sent', 'error')
  }

  const handleResendSuccess = () => {
    setResendModalState(false)
    displayToast('A new verification mail was successfully sent', 'success')
  }

  const handleVerifyModalState = (isOpen: boolean) => () => setVerifyModalState(isOpen)

  const handleVerifyConfirm = () => {
    displayToast('Email was successfully verified', 'success')
    setVerifyModalState(false)
  }

  const handleVerifyFailure = (message: string) => {
    setVerifyModalState(false)
    displayToast(message, 'error')
  }

  const handleGetTokenModalState = (isOpen: boolean) => () => setTokenModalState(isOpen)

  const handleGetTokenFailure = (message: string) => {
    setTokenModalState(false)
    displayToast(message, 'error')
  }

  const handleCreatePurchasePanelState = (isOpen: boolean) => () => setIsCreatePurchasePanelOpen(isOpen)

  const handleCreatePurchaseSuccess = (message: string) => {
    setIsCreatePurchasePanelOpen(false)
    displayToast(message, 'success')
  }

  const handleCreatePurchaseFailure = (message: string) => displayToast(message, 'error')

  const handleDeleteAccountModalState = (isOpen: boolean) => () => setDeleteAccountModalState(isOpen)

  const handleConfirmDeleteAccount = async () => {
    if (user) {
      try {
        await deleteAppsUserAccount({
          env,
          userId: user.user_id,
        })
        await queryClient.invalidateQueries({ queryKey: appsUsersKeys.search(search) })
        displayToast('The account was successfully deleted', 'success')
        setDeleteAccountModalState(false)
        navigate('/accounts', { replace: true })
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  return (
    <>
      <div className="view-layout">
        <div className="users-details-view">
          <ViewTitle className="layout-view-title flex justify-space-between">
            <BackNavigation title="Accounts" to={location.state?.from ? location.state.from : '/accounts'} />
            <div className="flex align-center justify-center">Account details</div>
            <div className="flex right last-seen">
              {user ? `Last seen on ${formatDate(user.last_seen, DATE_FORMATS.DEFAULT_WITH_HOURS)}` : null}
            </div>
          </ViewTitle>
          <div className="users-details-header-container">
            <div className="users-details-header">
              <EnvironmentBadge size="large" value={env} />
              ID: {userId}
              <Button ghost onClick={handleCopy(userId)}>
                <MdContentCopy color="#626262" size={18} />
              </Button>
            </div>
            <div className="status-delete-container">
              {user?.status.length ? <AppUserStatusBadge value={user.status} /> : null}
              <Button onClick={handleDeleteAccountModalState(true)} type="button">
                Delete
              </Button>
            </div>
          </div>
          <div className="layout-content">
            {isLoading ? (
              <MwMLoader category="item" />
            ) : !!user ? (
              <>
                <DetailSection
                  title={`Email credentials (${user.email_credentials ? user.email_credentials.length : 0})`}
                >
                  {user.email_credentials ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="table-header table-position--left">Email</th>
                          <th className="table-header table-position--right">Created at</th>
                          <th className="table-header table-position--right">Updated at</th>
                          <th className="table-header" />
                        </tr>
                      </thead>
                      <tbody>
                        {user.email_credentials.map((creds, index) => (
                          <EmailCredentialsRow
                            credentials={creds}
                            key={`${creds.email}-${index}`}
                            setEmailRowActionMenu={setEmailRowActionMenu}
                          />
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </DetailSection>
                <DetailSection title={`Connections (${user.connections ? user.connections.length : 0})`}>
                  {user.connections ? (
                    <TableWrapper
                      allowContextMenuWithControlKey={true}
                      className="app-users-table__connections with-min-height"
                      columnDefs={connectionsColumns}
                      context={{ handleGetToken, type: 'connections' }}
                      entriesName="connections"
                      rowData={user.connections}
                      suppressRowHoverHighlight
                      tableRef={connectionsTableRef}
                      withPagination={false}
                    />
                  ) : (
                    <div className="no-data-message">No connection</div>
                  )}
                </DetailSection>
                <DetailSection
                  title={`Provider Credentials (${user.provider_credentials ? user.provider_credentials.length : 0})`}
                >
                  {user.provider_credentials ? (
                    <TableWrapper
                      allowContextMenuWithControlKey
                      className="app-users-table__credentials"
                      columnDefs={credentialsColumns}
                      entriesName="credentials"
                      rowData={user.provider_credentials}
                      suppressRowHoverHighlight
                      tableRef={credentialsTableRef}
                      withPagination={false}
                    />
                  ) : (
                    <div className="no-data-message">No provider credentials</div>
                  )}
                </DetailSection>
                <DetailSection title={`Features (${formattedFeatures ? formattedFeatures.length : 0})`}>
                  {formattedFeatures ? (
                    <TableWrapper
                      allowContextMenuWithControlKey
                      className="app-users-table__features with-min-height"
                      columnDefs={featuresColumns}
                      entriesName="features"
                      rowData={formattedFeatures}
                      suppressRowHoverHighlight
                      tableRef={featuresTableRef}
                      withPagination={false}
                    />
                  ) : (
                    <div className="no-data-message">No features</div>
                  )}
                </DetailSection>
                <DetailSection title={`Purchases (${user.purchases ? user.purchases.length : 0})`}>
                  <TableWrapper
                    addButton
                    addButtonLabel="Add purchase"
                    allowContextMenuWithControlKey
                    className="app-users-table__purchases with-min-height"
                    columnDefs={purchasesColumns}
                    context={{ handleDeletePurchase, handleEditPurchase, type: 'purchases' }}
                    entriesName="purchases"
                    onAddButtonClick={handleCreatePurchasePanelState(true)}
                    rowData={user.purchases}
                    suppressRowHoverHighlight
                    tableRef={purchasesTableRef}
                    withPagination={false}
                  />
                </DetailSection>
              </>
            ) : (
              <div>The user could not be found!</div>
            )}
          </div>
        </div>
      </div>
      <UpdatePurchaseModal
        env={env}
        isOpen={isUpdatePurchaseModalOpen}
        onClose={handleUpdatePurchaseModalState(false)}
        onFailure={handleEditFailure}
        onSuccess={handleEditSuccess}
        purchase={selectedPurchase}
        user={user}
      />
      <EditUserModal
        email={selectedEmail}
        env={env}
        isOpen={isEditModalOpen}
        onClose={handleEditModalState(false)}
        onFailure={handleEditFailure}
        onSuccess={handleEditSuccess}
        user={user}
      />
      <ModalDialog
        isOpen={isDeleteAccountModalOpen}
        mainText="Do you really want to delete this account"
        onClose={handleDeleteAccountModalState(false)}
        onConfirm={handleConfirmDeleteAccount}
      />
      <ModalDialog
        isOpen={isDeletePurchaseModalOpen}
        mainText="Do you really want to delete this purchase"
        onClose={handleDeletePurchaseModalState(false)}
        onConfirm={handleDeletePurchaseConfirm}
      />
      {user ? (
        <ResendVerificationMailModal
          email={selectedEmail}
          env={env}
          isOpen={isResendModalOpen}
          onClose={handleResendModalState(false)}
          onFailure={handleResendFailure}
          onSuccess={handleResendSuccess}
          user={user}
        />
      ) : null}
      <VerifyMailModal
        email={selectedEmail}
        env={env}
        isOpen={isVerifyModalOpen}
        onClose={handleVerifyModalState(false)}
        onFailure={handleVerifyFailure}
        onSuccess={handleVerifyConfirm}
        user={user}
      />
      <GetTokenModal
        connectionData={connectionData}
        env={env}
        isOpen={isTokenModalOpen}
        onClose={handleGetTokenModalState(false)}
        onFailure={handleGetTokenFailure}
        onSuccess={noOp}
        user={user}
      />
      <Panel
        className="create-iap-panel"
        isOpen={isCreatePurchasePanelOpen}
        onClosePanel={handleCreatePurchasePanelState(false)}
        panelRef={contentRefCallback}
      >
        <PanelContent
          env={env}
          onClosePanel={handleCreatePurchasePanelState(false)}
          onFailure={handleCreatePurchaseFailure}
          onSuccess={handleCreatePurchaseSuccess}
          userId={user?.user_id}
        />
      </Panel>
    </>
  )
}

export default AppsUsersDetails
